
import {DatumAnchorPanel} from "./PropertyPanels/DatumAnchorPanel.js";

/**
 * A small HTML overlay that tracks a landmark's 3D position on screen.
 * Shows action icons (datum anchor, distance toggle, close) near the landmark.
 */
export class LandmarkPopover {

	constructor(viewer) {
		this.viewer = viewer;
		this.zone = null;
		this.distanceTool = null;
		this._rafId = null;
		this._activePanel = null;

		// Create the popover element
		this.el = document.createElement('div');
		this.el.id = 'cyvl_landmark_popover';
		this.el.style.display = 'none';

		// Action buttons
		let anchorIcon = Potree.resourcePath + '/icons/anchor.svg';
		let rulerIcon = Potree.resourcePath + '/icons/ruler.svg';

		this.el.innerHTML = `
			<button class="popover-btn" id="popover_anchor" title="Datum Anchor">
				<img src="${anchorIcon}" />
			</button>
			<button class="popover-btn" id="popover_ruler" title="Toggle Distance Group">
				<img src="${rulerIcon}" />
			</button>
			<button class="popover-btn" id="popover_close" title="Close">
				&#10005;
			</button>
		`;

		// Append to render area so it overlays the 3D scene
		let renderArea = document.getElementById('potree_render_area');
		if (renderArea) {
			renderArea.appendChild(this.el);
		}

		// Wire up button clicks
		this.el.querySelector('#popover_close').addEventListener('click', () => {
			this.hide();
		});

		this.el.querySelector('#popover_ruler').addEventListener('click', () => {
			if (this.zone && this.distanceTool) {
				this.distanceTool.toggleInGroup(this.zone);
			}
		});

		this.el.querySelector('#popover_anchor').addEventListener('click', () => {
			if (!this.zone || this.zone.derivedFrom) return;

			if (this._activePanel) {
				this._activePanel.elContent.remove();
				this._activePanel = null;
				return;
			}

			let panel = new DatumAnchorPanel(this.viewer, this.zone);
			panel.onClose = () => {
				this._activePanel = null;
			};

			// Append datum panel below the popover
			this.el.appendChild(panel.elContent[0] || panel.elContent);
			this._activePanel = panel;
		});
	}

	/**
	 * Show the popover tracking a specific feature zone.
	 */
	show(zone, distanceTool) {
		this.zone = zone;
		this.distanceTool = distanceTool;

		// Hide derived landmark anchor button
		let anchorBtn = this.el.querySelector('#popover_anchor');
		anchorBtn.style.display = zone.derivedFrom ? 'none' : '';

		// Close any open datum panel from previous zone
		if (this._activePanel) {
			this._activePanel.elContent.remove();
			this._activePanel = null;
		}

		this.el.style.display = 'flex';
		this._startTracking();
	}

	/**
	 * Hide the popover and stop tracking.
	 */
	hide() {
		this.el.style.display = 'none';
		this.zone = null;

		if (this._activePanel) {
			this._activePanel.elContent.remove();
			this._activePanel = null;
		}

		if (this._rafId) {
			cancelAnimationFrame(this._rafId);
			this._rafId = null;
		}
	}

	/**
	 * Start the animation frame loop that projects the zone position to screen.
	 */
	_startTracking() {
		if (this._rafId) {
			cancelAnimationFrame(this._rafId);
		}

		let update = () => {
			if (!this.zone) return;

			let pos = this._projectToScreen(this.zone.position);

			if (pos.behind) {
				this.el.style.display = 'none';
			} else {
				this.el.style.display = 'flex';
				this.el.style.left = pos.x + 'px';
				this.el.style.top = pos.y + 'px';
			}

			this._rafId = requestAnimationFrame(update);
		};

		this._rafId = requestAnimationFrame(update);
	}

	/**
	 * Project a world-space position to screen coordinates within the render area.
	 */
	_projectToScreen(worldPos) {
		let camera = this.viewer.scene.getActiveCamera();
		let container = this.viewer.renderer.domElement;

		let pos = worldPos.clone().project(camera);

		let w = container.clientWidth;
		let h = container.clientHeight;

		return {
			x: (pos.x * 0.5 + 0.5) * w,
			y: (-pos.y * 0.5 + 0.5) * h,
			behind: pos.z > 1.0
		};
	}
}
